'use client';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';

const MAX_RATIO = 9999;
const MIN_RATIO = 1;

/**
 * 处理输入值变化，检查是否在 MIN_RATIO 和 MAX_RATIO 范围内
 * @param value 输入的字符串值（非空）
 * @returns 处理后的数字值，如果输入无效或超出范围则返回 null
 */
const clampInputValue = (value: string): number | null => {
  const numValue = Number.parseInt(value, 10);
  // 如果是有效数字，检查是否在范围内
  if (!Number.isNaN(numValue)) {
    // 如果超出范围，返回 null（不更新状态，维持上一个值）
    if (numValue < MIN_RATIO || numValue > MAX_RATIO) {
      return null;
    }
    return numValue;
  }

  // 如果不是有效数字，返回 null
  return null;
};

/**
 * 自定义宽高比对话框组件
 */
export const CustomAspectRatioDialog: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (wide: number, high: number) => void;
  initialWide?: number;
  initialHigh?: number;
}> = ({ open, onOpenChange, onConfirm, initialWide, initialHigh }) => {
  const t = useTranslations();
  const [wide, setWide] = useState<number | undefined>(initialWide);
  const [high, setHigh] = useState<number | undefined>(initialHigh);
  const [error, setError] = useState<string>('');

  // 弹窗常驻挂载，初始值只在 mount 时进 state；每次打开都要重新回填当前生效的比例，
  // 否则用户上次取消/改动留下的草稿会一直显示。
  useEffect(() => {
    if (!open) {
      return;
    }

    setWide(initialWide);
    setHigh(initialHigh);
    setError('');
  }, [open, initialWide, initialHigh]);

  const validateRatio = (w: number, h: number): boolean => {
    const ratio = w / h;
    if (ratio > 10 || ratio < 0.1) {
      setError(t('create.createForm.settings.aspectRatio.custom.error.ratioLimit'));
      return false;
    }
    setError('');
    return true;
  };

  const handleWideChange = (value: string) => {
    // 先判断空字符串，允许清空输入
    if (value === '') {
      setWide(undefined);
      setError('');
      return;
    }

    // 检查输入值是否在有效范围内
    const clampedValue = clampInputValue(value);
    if (clampedValue !== null) {
      setWide(clampedValue);
      if (high) {
        validateRatio(clampedValue, high);
      } else {
        setError('');
      }
    }
    // 如果输入超出范围或无效，不更新状态，维持上一个值
  };

  const handleHighChange = (value: string) => {
    // 先判断空字符串，允许清空输入
    if (value === '') {
      setHigh(undefined);
      setError('');
      return;
    }

    // 检查输入值是否在有效范围内
    const clampedValue = clampInputValue(value);
    if (clampedValue !== null) {
      setHigh(clampedValue);
      if (wide) {
        validateRatio(wide, clampedValue);
      } else {
        setError('');
      }
    }
    // 如果输入超出范围或无效，不更新状态，维持上一个值
  };

  const handleConfirm = () => {
    if (wide && high && validateRatio(wide, high)) {
      onConfirm(wide, high);
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setWide(initialWide);
    setHigh(initialHigh);
    setError('');
    onOpenChange(false);
  };

  const isConfirmDisabled = !!error || !wide || !high;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90%] max-w-[400px] p-6 sm:p-6" closable>
        <DialogHeader>
          <DialogTitle className="text-system-text01 text-xl">
            {t('create.createForm.settings.aspectRatio.custom.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-4">
              <label htmlFor="custom-aspect-ratio-wide" className="text-sm text-system-white64">
                {t('create.createForm.settings.aspectRatio.custom.wide')}
              </label>
              <Input
                id="custom-aspect-ratio-wide"
                type="number"
                value={wide?.toString() ?? ''}
                onChange={(e) => handleWideChange(e.target.value)}
                className="flex-1"
                min={MIN_RATIO}
                step="1"
                max={MAX_RATIO}
              />
            </div>
            <div className="flex items-center gap-4">
              <label htmlFor="custom-aspect-ratio-high" className="text-sm text-system-white64">
                {t('create.createForm.settings.aspectRatio.custom.high')}
              </label>
              <Input
                id="custom-aspect-ratio-high"
                type="number"
                value={high?.toString() ?? ''}
                onChange={(e) => handleHighChange(e.target.value)}
                className="flex-1"
                min={MIN_RATIO}
                step="1"
                max={MAX_RATIO}
              />
            </div>
            {error && <p className="text-sm text-system-danger">{error}</p>}
            <DialogDescription className="text-system-white64 text-xs">
              {t('create.createForm.settings.aspectRatio.custom.description')}
            </DialogDescription>
          </div>
        </div>
        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" size="dialog" onClick={handleCancel}>
            {t('create.createForm.settings.aspectRatio.custom.cancel')}
          </Button>
          <Button variant="primary" size="dialog" onClick={handleConfirm} disabled={isConfirmDisabled}>
            {t('create.createForm.settings.aspectRatio.custom.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
